/**
 * 맵 에디터에 등록된 맵을 파일 보관함 `map` 카테고리로 가져오는 다이얼로그.
 *
 * 파일 보관함의 map 카테고리와 맵 에디터의 저장 맵(map_projects/map_project_data)은
 * 서로 다른 저장소다. 지금까지는 에디터 맵을 보관함에 넣으려면 사람이 직접
 * export 해서 다시 업로드해야 했다 — 여기서 그 왕복을 없앤다.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { SimulatorService } from '../../services/SimulatorService';
import { SimulatorMap } from '../../models/Simulator';
import mapDataService from '../../map-editor-shared/api/services/mapDataService';

interface MapEditorImportDialogProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
  /** 맵 JSON 을 파일로 만들어 넘긴다 — 업로드 경로는 일반 업로드와 동일. */
  onImport: (file: File) => void;
}

/** 파일명에 쓸 수 없는 문자를 걷어낸다. */
export const safeFileName = (name: string): string =>
  name.replace(/[^\w가-힣.-]+/g, '_').replace(/^_+|_+$/g, '') || 'map';

const MapEditorImportDialog: React.FC<MapEditorImportDialogProps> = ({
  open,
  projectId,
  onClose,
  onImport,
}) => {
  const [maps, setMaps] = useState<SimulatorMap[]>([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoading(true);
    SimulatorService.listMaps(Number(projectId))
      .then(setMaps)
      .catch((e) => setError(e?.message || '맵 목록을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const handleSelect = useCallback(
    async (map: SimulatorMap) => {
      setError(null);
      setImportingId(map.id);
      try {
        const response = await mapDataService.getMapData(map.id);
        const raw = response.success ? response.data?.[0]?.map_data : undefined;
        if (!raw) {
          setError(`"${map.name}" 에 저장된 맵 데이터가 없습니다.`);
          return;
        }
        onImport(
          new File([raw], `${safeFileName(map.name)}.json`, {
            type: 'application/json',
          }),
        );
        onClose();
      } catch (e: any) {
        setError(e?.message || '맵 데이터를 불러오지 못했습니다.');
      } finally {
        setImportingId(null);
      }
    },
    [onImport, onClose],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>맵 에디터에서 가져오기</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : maps.length === 0 ? (
          <Alert severity="info">이 현장에 등록된 맵이 없습니다.</Alert>
        ) : (
          <List dense disablePadding>
            {maps.map((map) => (
              <ListItemButton
                key={map.id}
                disabled={importingId !== null}
                onClick={() => handleSelect(map)}
              >
                <ListItemText primary={map.name} secondary={map.description} />
                {importingId === map.id && <CircularProgress size={18} />}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapEditorImportDialog;
